import React, { useCallback } from 'react';
import { useAuth } from "react-oidc-context";
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  padding: 2rem;
  font-family: ${theme.fonts.main};

  @media (max-width: 768px) {
    min-height: calc(100vh - 56px);
    padding: 1rem;
    justify-content: center;
  }
`;

const LoginCard = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 440px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 16px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  }
`;

const Title = styled.h1`
  color: ${theme.colors.primary};
  margin-bottom: 1rem;
  font-size: 2rem;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Description = styled.p`
  color: ${theme.colors.text};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const LoginButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  min-height: 52px;

  &:hover {
    background: #2c3e8a;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 1.1rem 2rem;
    font-size: 1.15rem;
    min-height: 56px;
  }
`;

const StatusMessage = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background: #f3f4f6;
  color: ${theme.colors.text};
`;

const Login = () => {
  const auth = useAuth();

  const clearAuthState = useCallback(() => {
    // Clear OIDC stale state from storage to fix cookie/session issues
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('oidc.')) keysToRemove.push(key);
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));

    const localKeysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('oidc.')) localKeysToRemove.push(key);
    }
    localKeysToRemove.forEach(key => localStorage.removeItem(key));

    auth.removeUser();
    window.location.reload();
  }, [auth]);

  // 如果已经登录，重定向到登录后主页（旧的 /dashboard 已停用）
  if (auth.isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  if (auth.isLoading) {
    return (
      <LoginContainer>
        <LoginCard>
          <Title>Loading...</Title>
          <Description>Please wait while we prepare the login page.</Description>
        </LoginCard>
      </LoginContainer>
    );
  }

  if (auth.error) {
    return (
      <LoginContainer>
        <LoginCard>
          <Title>Login Error</Title>
          <Description>
            An error occurred during login. Please try again.
          </Description>
          <StatusMessage>
            Error: {auth.error.message}
          </StatusMessage>
          <LoginButton onClick={clearAuthState}>
            Clear Session & Retry
          </LoginButton>
        </LoginCard>
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <LoginCard>
        <Title>Welcome to Uplus Education</Title>
        <Description>
          Please sign in to access your account and view personalized content.
        </Description>
        <LoginButton onClick={() => auth.signinRedirect()}>
          Sign In
        </LoginButton>
        <StatusMessage>
          You will be redirected to our secure login page.
        </StatusMessage>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;